/**
 * AdminUserInviteModal — ADR-0022.
 *
 * Super-admin tworzy nowego administratora platformy. POST /api/admin/admins
 * z body { email, username, name?, role }. Backend wysyla mail z linkiem
 * accept-invite (TTL 24h) — admin tworzy haslo sam.
 *
 * Walidacja FE:
 *   - email: format
 *   - username: 3-32 znakow, [a-z0-9._-], no leading/trailing punctuation
 *   - role: enum
 *
 * Error mapping:
 *   409 EMAIL_ALREADY_EXISTS → inline error pola email
 *   409 USERNAME_ALREADY_EXISTS → inline error pola username
 *   422 VALIDATION_FAILED → toast generic
 */

import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidUsername } from '@saas/shared';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export type AdminRole = 'super_admin' | 'support' | 'finance' | 'readonly';

export interface CreatedAdmin {
  id: string;
  email: string;
  username: string;
  name: string | null;
  role: AdminRole;
  isActive: boolean;
  invitedAt: string;
  invitationExpiresAt: string;
}

export interface AdminUserInviteModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (admin: CreatedAdmin) => void;
}

interface FieldState {
  email: string;
  username: string;
  name: string;
  role: AdminRole;
}

function emptyFields(): FieldState {
  return { email: '', username: '', name: '', role: 'readonly' };
}

const FORM_ID = 'admin-invite-form';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AdminUserInviteModal({ open, onClose, onSuccess }: AdminUserInviteModalProps) {
  const { t } = useTranslation();
  const formId = useId();
  const resolvedFormId = FORM_ID + '-' + formId;

  const [fields, setFields] = useState(emptyFields());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setFields(emptyFields());
      setIsSubmitting(false);
      setApiError(null);
      setFieldErrors({});
    }
    prevOpenRef.current = open;
  }, [open]);

  function setField<K extends keyof FieldState>(key: K, value: FieldState[K]) {
    setFields((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    if (apiError) setApiError(null);
  }

  function validate(): boolean {
    const errors: Record<string, string> = {};

    const email = fields.email.trim().toLowerCase();
    if (!email) {
      errors.email = t('admin.adminUsers.invite.errors.emailRequired');
    } else if (!EMAIL_REGEX.test(email)) {
      errors.email = t('admin.adminUsers.invite.errors.emailFormat');
    }

    const username = fields.username.trim();
    if (!username) {
      errors.username = t('admin.adminUsers.invite.errors.usernameRequired');
    } else if (!isValidUsername(username)) {
      errors.username = t('admin.adminUsers.invite.errors.usernameFormat');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);
    setApiError(null);

    const payload: Record<string, unknown> = {
      email: fields.email.trim().toLowerCase(),
      username: fields.username.trim(),
      role: fields.role,
    };
    const name = fields.name.trim();
    if (name) payload.name = name;

    try {
      const res = await api.post<CreatedAdmin>('/admin/admins', payload);
      onSuccess(res.data);
    } catch (err) {
      const axErr = err as AxiosError<{ error: string }>;
      const code = axErr.response?.data?.error;
      if (code === 'EMAIL_ALREADY_EXISTS') {
        setFieldErrors((prev) => ({
          ...prev,
          email: t('admin.adminUsers.invite.errors.emailTaken'),
        }));
      } else if (code === 'USERNAME_ALREADY_EXISTS') {
        setFieldErrors((prev) => ({
          ...prev,
          username: t('admin.adminUsers.invite.errors.usernameTaken'),
        }));
      } else if (code === 'VALIDATION_FAILED') {
        setApiError(t('admin.adminUsers.invite.errors.validation'));
      } else if (axErr.response?.status === 401 || axErr.response?.status === 403) {
        setApiError(t('admin.adminUsers.invite.errors.forbidden'));
      } else {
        setApiError(t('admin.adminUsers.invite.errors.unknown'));
      }
      setIsSubmitting(false);
    }
  }

  const roleOptions: { value: AdminRole; label: string }[] = [
    { value: 'super_admin', label: t('admin.adminUsers.role.super_admin') },
    { value: 'support', label: t('admin.adminUsers.role.support') },
    { value: 'finance', label: t('admin.adminUsers.role.finance') },
    { value: 'readonly', label: t('admin.adminUsers.role.readonly') },
  ];

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
        {t('admin.adminUsers.invite.buttonCancel')}
      </Button>
      <Button type="submit" form={resolvedFormId} loading={isSubmitting} disabled={isSubmitting}>
        {isSubmitting
          ? t('admin.adminUsers.invite.buttonSubmitting')
          : t('admin.adminUsers.invite.buttonSubmit')}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.adminUsers.invite.title')}
      description={t('admin.adminUsers.invite.description')}
      size="md"
      footer={footer}
    >
      <form id={resolvedFormId} onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Input
          label={t('admin.adminUsers.invite.emailLabel')}
          type="email"
          autoComplete="email"
          required
          value={fields.email}
          onChange={(e) => setField('email', e.target.value)}
          placeholder="admin@firma.pl"
          error={fieldErrors.email}
          disabled={isSubmitting}
          data-testid="admin-invite-email"
        />
        <Input
          label={t('admin.adminUsers.invite.usernameLabel')}
          type="text"
          autoComplete="off"
          required
          value={fields.username}
          onChange={(e) => setField('username', e.target.value)}
          placeholder="login"
          hint={t('admin.adminUsers.invite.usernameHint')}
          error={fieldErrors.username}
          disabled={isSubmitting}
          data-testid="admin-invite-username"
        />
        <Input
          label={t('admin.adminUsers.invite.nameLabel')}
          type="text"
          autoComplete="name"
          value={fields.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder={t('admin.adminUsers.invite.namePlaceholder')}
          disabled={isSubmitting}
          data-testid="admin-invite-name"
        />
        <FormField label={t('admin.adminUsers.invite.roleLabel')}>
          <Dropdown
            value={fields.role}
            onChange={(v) => setField('role', v as AdminRole)}
            options={roleOptions}
            disabled={isSubmitting}
            data-testid="admin-invite-role"
          />
        </FormField>

        {apiError && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            data-testid="admin-invite-api-error"
          >
            {apiError}
          </div>
        )}
      </form>
    </Modal>
  );
}
